import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TourItem {
  id: string;
  name: string;
  status: string;
  passEnabled: boolean;
  createdAt: string;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ToursListPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const [deleteTarget, setDeleteTarget] = useState<TourItem | null>(null);

  const { data = [], isLoading } = useQuery<TourItem[]>({
    queryKey: ['tours'],
    queryFn: () =>
      api.get('/api/v1/events/tours').then((r) => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/events/tours/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tours'] });
      setDeleteTarget(null);
      toast.success('Tour deleted');
    },
    onError: (err) => toast.error(extractError(err, 'Error deleting tour')),
  });

  return (
    <div>
      <PageHeader
        title="Tour"
        action={
          <Button onClick={() => navigate('/events/tours/new')} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Tour
          </Button>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={4} cols={4} />
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={<MapPin size={26} />}
          title="No tours found"
          description="Create your first tour to group events in sequence"
          action={{ label: 'New Tour', onClick: () => navigate('/events/tours/new') }}
        />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Pass enabled
                </th>
                <th className="px-4 py-3 w-16" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.map((tour) => (
                <tr
                  key={tour.id}
                  className="hover:bg-zinc-50 transition-colors group cursor-pointer"
                  onClick={() => navigate(`/events/tours/${tour.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900">{tour.name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={tour.status === 'active' ? 'success' : 'default'}>
                      {tour.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-zinc-500">
                    {tour.passEnabled ? (
                      <Badge variant="info">Yes</Badge>
                    ) : (
                      <span className="text-zinc-400 text-xs">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(tour);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete tour?"
        message={`"${deleteTarget?.name}" will be permanently deleted.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
